import React, { useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import { TabPanelCollapsedValue, VerticalTabs } from '../../../../components/Tabs/VerticalTabs/VerticalTabs.component';
import { SessionsParametersCardLimit } from '../SessionsParametersCardLimit/SessionsParametersCardLimit.component';
import { CardLimits } from './SessionsParametersDialogContent.types';
import { getCardLimits } from '../../../../services/exams/sessions/sessions.service';
import {
  getExaminersBlockingLimitsPerSession,
  getProcessLimits,
} from '../../../../services/exams/assessmentSession/assessments.service';
import { SessionProcessLimits } from '../SessionsProcessLimits/SessionsProcessLimits.component';
import { SessionsProcessSchedule } from '../SessionsProcessSchedule/SessionsProcessSchedule.component';
import {
  ProcessLimitRow,
  AssessmentSessionProcessLimits,
  ProcessLimits,
} from '../SessionsProcessLimits/SessionsProcessLimits.types';
import usePermission from '../../../../hooks/usePermission';
import { UserPermission } from '../../../../enum/userPermissions';
import { TabItem } from '../../../../components/Tabs/VerticalTabs/VerticalTabs.types';
import { ToleranceRange } from '../ToleranceRange/ToleranceRange.component';
import { ExaminerBlockingLimit } from '../ExaminerBlockingLimits/ExaminerBlockingLimits.types';
import useDocumentTitle from '../../../../hooks/DocumentTitle/documentTitle';
import {
  getControlTasksDistribution,
  getExaminersBlockingLimitsBySheetId,
  getQualificationThresholds,
} from '../../../../services/exams/examSheet/examSheet.service';
import { AxiosResponse } from 'axios';
import { ExaminerFunction } from '../../../Assessment/Assessment.types';
import { QualificationThreshold } from '../QualitifcationThreshold/QualificationThreshold.component';
import { QualificationThresholds } from '../QualitifcationThreshold/QualificationThreshold.types';
import { ControlTasksDistribution } from '../ControlTasksDistribution/ControlTasksDistribution.component';
import { IControlTasksDistribution } from '../ControlTasksDistribution/ControlTasksDistribution.types';
import { AssessmentSessionProcessTypeId } from '../../../../services/exams/assessmentSession/assessments.types';
import { ExaminerIndividualBlockingLimitsTable } from '../ExaminerIndividualBlockingLimits/ExaminerIndividualBlockingLimitsTable.component';
import { ExaminerBlockingLimits } from '../ExaminerBlockingLimits/ExaminerBlockingLimits.component';
import useSessionUser from '../../../../hooks/useSessionUser';
import { useTranslation } from 'react-i18next';

interface SessionsParametersDialogContentProps {
  sheetId: string;
  onClose: () => void;
  onCancel: () => void;
  onChange: (isDataChanged: boolean) => void;
  activeTab: string;
  isSheetConfigured: boolean;
  isExaminerView?: boolean;
  examinerFunction?: ExaminerFunction;
  isControlProcessActive?: boolean;
  isQualificationProcessActive?: boolean;
}

export const SessionsParametersDialogContent: React.FC<SessionsParametersDialogContentProps> = ({
  sheetId,
  onClose,
  onChange,
  activeTab,
  isExaminerView,
  examinerFunction,
  isSheetConfigured,
  isControlProcessActive,
  isQualificationProcessActive,
}) => {
  const [cardLimits, setCardLimits] = useState<CardLimits[]>([]);
  const [processLimits, setProcessLimits] = useState<ProcessLimitRow[]>([]);
  const [qualificationThreshold, setQualificationThreshold] = useState<QualificationThresholds[]>([]);
  const [apiResponse, setApiResponse] = useState<CardLimits[]>([]);
  const [reloadCardLimits, setReloadCardLimits] = useState<boolean>(false);
  const [isCardLimitLoading, setIsCardLimitLoading] = useState<boolean>(true);
  const [reloadProcessLimits, setReloadProcessLimits] = useState<boolean>(false);
  const [reloadThreshold, setReloadThreshold] = useState<boolean>(false);
  const [examinerBlockingLimits, setExaminerBlockingLimits] = useState<ExaminerBlockingLimit[]>([]);
  const [isExaminerBlockingLimitsLoading, setIsExaminerBlockingLimitsLoading] = useState<boolean>(true);
  const [apiResponseBlockingLimits, setApiResponseBlockingLimits] = useState<string>('');
  const [processLimitsApiResponse, setProcessLimitsApiResponse] = useState<string>('');
  const [qualificationTresholdsApiResponse, setQualificationTresholdsApiResponse] = useState<string>('');
  const [isProcessLimitsLoading, setIsProcessLimitsLoading] = useState<boolean>(true);
  const [isQualificationThresholdLoading, setIsQualificationThresholdLoading] = useState<boolean>(true);
  const [isControlTasksDistributionLoading, setIsControlTasksDistributionLoading] = useState<boolean>(true);
  const [apiResponseControlTasksDistribution, setApiResponseControlTasksDistribution] = useState<string>('');
  const [reloadControlTasks, setReloadControlTasks] = useState<boolean>(false);
  const [controlTasksDistribution, setControlTasksDistribution] = useState<IControlTasksDistribution[]>([]);
  const [examTaskGroups, setExamTaskGroups] =
    useState<{ id: string; name: string; isControlProcessRequired: boolean }[]>();

  const { isUserKK, isUserKGZ } = useSessionUser();
  const { t } = useTranslation();

  const tabLabels = {
    sessionsParametersCardLimit: t('sessionsParameters:tabLabels.sessionsParametersCardLimit'),
    sessionProcessLimits: t('sessionsParameters:tabLabels.sessionProcessLimits'),
    sessionsProcessSchedule: t('sessionsParameters:tabLabels.sessionsProcessSchedule'),

    controllProcess: t('sessionsParameters:tabLabels.controllProcess'),
    controllToleranceRange: t('sessionsParameters:tabLabels.controllToleranceRange'),
    controlTasksDistribution: t('sessionsParameters:tabLabels.controlTasksDistribution'),
    examinerBlockingLimits: t('sessionsParameters:tabLabels.examinerBlockingLimits'),
    examinerIndividualBlockingLimits: t('sessionsParameters:tabLabels.examinerIndividualBlockingLimits'),

    qualificationProcess: t('sessionsParameters:tabLabels.qualificationProcess'),
    qualificationToleranceRange: t('sessionsParameters:tabLabels.qualificationToleranceRange'), //space added due to labels comparison in tab component => needs to be different to controllToleranceRange
    qualificationThreshold: t('sessionsParameters:tabLabels.qualificationThreshold'),
  };

  const hasAdminSessionParamsManagePermission =
    usePermission(UserPermission.AdminSessionParamsManage) ||
    (isExaminerView && examinerFunction === ExaminerFunction.KK);
  const hasAdminSessionScheduleManagePermission =
    usePermission(UserPermission.AdminSessionScheduleManage) ||
    (isExaminerView && examinerFunction === ExaminerFunction.KK);
  const hasAdminSessionStatusManagePermissionControlProcess =
    usePermission(UserPermission.AdminSessionStatusManage) || (isExaminerView && isControlProcessActive);
  const hasAdminSessionStatusManagePermissionQualificationProcess =
    usePermission(UserPermission.AdminSessionStatusManage) || (isExaminerView && isQualificationProcessActive);
  const hasAdminPermissionProcessLimit =
    usePermission(UserPermission.AdminSessionStatusManage) ||
    (isExaminerView && examinerFunction === ExaminerFunction.KK);
  const convertProcesses = (data: AssessmentSessionProcessLimits): ProcessLimitRow => {
    return {
      examTaskGroupId: data.examTaskGroupId ? data.examTaskGroupId : '',
      examTaskGroupName: data.examTaskGroupName ? data.examTaskGroupName : '',
      trainingProcessId: data.training.assessmentSessionProcessId,
      isPZETrainingLimit: data.training.haveExaminingTeamChairmanRequiredSolutions ?? false,
      trainingPZELimitValue: data.training.examiningTeamChairmanRequiredSolutionsCount ?? 0,
      isExaminerTrainingLimit: data.training.haveExamingTeamMemberRequiredSolutions ?? false,
      trainingExaminerLimitValue: data.training.examingTeamMemberRequiredSolutionsCount ?? 0,
      trainingZWOLimitValue: data.training.sampleAssessmentGroupMemberRequiredSolutionsCount ?? 0,
      qualificationProcessId: data.qualification.assessmentSessionProcessId,
      isPZEQualificationLimit: data.qualification.haveExaminingTeamChairmanRequiredSolutions ?? false,
      qualificationPZElimitValue: data.qualification.examiningTeamChairmanRequiredSolutionsCount ?? 0,
      isExaminerQualificationLimit: data.qualification.haveExamingTeamMemberRequiredSolutions ?? false,
      qualificationExaminerLimitValue: data.qualification.examingTeamMemberRequiredSolutionsCount ?? 0,
      qualificationZWOlimitValue: data.qualification.sampleAssessmentGroupMemberRequiredSolutionsCount ?? 0,
      controlProcessId: data.control.assessmentSessionProcessId,
      isExaminerControlLimit: data.control.haveExamingTeamMemberRequiredSolutions ?? false,
      controlZWOLimitValue: data.control.sampleAssessmentGroupMemberRequiredSolutionsCount ?? 0,
      assessmentSessionStateId: data.assessmentSessionStateId,
    };
  };
  useDocumentTitle('Ustaw parametry sesji oceniania');
  useEffect(() => {
    if (sheetId && hasAdminPermissionProcessLimit) {
      getCardLimits(sheetId, isExaminerView)
        .then((response) => {
          setCardLimits(response.cardLimits);
          const cardLimitsCopy = JSON.parse(JSON.stringify(response.cardLimits));
          setApiResponse(cardLimitsCopy);
        })
        .catch(() => {
          setCardLimits([]);
        })
        .finally(() => {
          setIsCardLimitLoading(false);
          setReloadCardLimits(false);
        });
    }
  }, [sheetId, reloadCardLimits, hasAdminPermissionProcessLimit]);

  useEffect(() => {
    if (sheetId && hasAdminSessionParamsManagePermission) {
      getProcessLimits(sheetId, isExaminerView)
        .then((res: ProcessLimits) => {
          const convertedData = res.assessmentSessionProcesses.map((e) => convertProcesses(e));
          setProcessLimits(convertedData);
          setProcessLimitsApiResponse(JSON.stringify(convertedData));
        })
        .catch((e) => console.error({ e }))
        .finally(() => {
          setIsProcessLimitsLoading(false);
          setReloadProcessLimits(false);
        });
    }
  }, [sheetId, reloadProcessLimits, hasAdminSessionParamsManagePermission]);

  useEffect(() => {
    if (sheetId && !isExaminerView) {
      getExaminersBlockingLimitsBySheetId(sheetId)
        .then((res: AxiosResponse<ExaminerBlockingLimit[]>) => {
          setExaminerBlockingLimits(res.data);
          const examinerBlockingLimitsCopy = JSON.parse(JSON.stringify(res.data));
          setApiResponseBlockingLimits(examinerBlockingLimitsCopy);
          setExamTaskGroups(
            res.data.map((e) => {
              return { id: e.examTaskGroupId, name: e.examTaskGroupName, isControlProcessRequired: e.isProcessActive };
            }),
          );
        })
        .catch((e) => console.error({ e }))
        .finally(() => {
          setIsExaminerBlockingLimitsLoading(false);
        });
    } else if (sheetId && isExaminerView) {
      getExaminersBlockingLimitsPerSession()
        .then((res: AxiosResponse<ExaminerBlockingLimit>) => {
          setExaminerBlockingLimits([res.data]);
          const examinerBlockingLimitsCopy = JSON.parse(JSON.stringify([res.data]));
          setApiResponseBlockingLimits(examinerBlockingLimitsCopy);
          setExamTaskGroups([
            {
              id: res.data.examTaskGroupId,
              name: res.data.examTaskGroupName,
              isControlProcessRequired: res.data.isProcessActive,
            },
          ]);
        })
        .catch((e) => console.error({ e }))
        .finally(() => {
          setIsExaminerBlockingLimitsLoading(false);
          setReloadProcessLimits(false);
        });
    }
  }, [sheetId, hasAdminSessionParamsManagePermission, reloadProcessLimits]);

  useEffect(() => {
    if (sheetId) {
      getQualificationThresholds(sheetId, isExaminerView)
        .then((res: QualificationThresholds[]) => {
          setQualificationThreshold(res);
          setQualificationTresholdsApiResponse(JSON.stringify(res));
        })
        .finally(() => {
          setIsQualificationThresholdLoading(false);
          setReloadThreshold(false);
          setReloadProcessLimits(false);
        });
    }
  }, [sheetId, hasAdminSessionStatusManagePermissionQualificationProcess, reloadThreshold, reloadProcessLimits]);

  useEffect(() => {
    if (sheetId) {
      getControlTasksDistribution(sheetId, isExaminerView)
        .then((res: IControlTasksDistribution[]) => {
          setControlTasksDistribution(res);
          setApiResponseControlTasksDistribution(JSON.stringify(res));
        })
        .finally(() => {
          setIsControlTasksDistributionLoading(false);
          setReloadControlTasks(false);
          setReloadProcessLimits(false);
        });
    }
  }, [sheetId, hasAdminSessionStatusManagePermissionControlProcess, reloadControlTasks, reloadProcessLimits]);

  const items: TabItem[] = [];

  const processControlTabs = [
    tabLabels.controllToleranceRange,
    tabLabels.controlTasksDistribution,
    tabLabels.examinerBlockingLimits,
    tabLabels.examinerIndividualBlockingLimits,
  ];
  if (hasAdminPermissionProcessLimit)
    items.push({
      label: tabLabels.sessionsParametersCardLimit,
      children: (
        <SessionsParametersCardLimit
          apiResponse={JSON.stringify(apiResponse)}
          items={cardLimits}
          onClose={onClose}
          reload={() => {
            setReloadCardLimits(true);
            setReloadControlTasks(true);
          }}
          onChange={onChange}
          isLoading={isCardLimitLoading}
          isExaminerView={Boolean(isExaminerView)}
          assessmentSessionStateId={processLimits[0]?.assessmentSessionStateId}
          sheetId={sheetId}
        />
      ),
    });

  if (hasAdminSessionParamsManagePermission)
    items.push({
      label: tabLabels.sessionProcessLimits,
      children: (
        <SessionProcessLimits
          items={processLimits}
          onClose={onClose}
          reload={() => {
            setReloadProcessLimits((prev) => !prev);
          }}
          actualProcessLimits={processLimitsApiResponse}
          onChange={onChange}
          isLoading={isProcessLimitsLoading}
          isExaminerView={Boolean(isExaminerView)}
        />
      ),
    });

  if (hasAdminSessionScheduleManagePermission)
    items.push({
      label: tabLabels.sessionsProcessSchedule,
      children: (
        <SessionsProcessSchedule
          sheetId={sheetId}
          onClose={onClose}
          isExaminerView={Boolean(isExaminerView)}
          onChange={onChange}
        />
      ),
    });

  if (hasAdminSessionStatusManagePermissionControlProcess && isSheetConfigured) {
    items.push({
      label: tabLabels.controllProcess,
      collapsable: true,
      collapsedListItems:
        !isExaminerView || examinerFunction === ExaminerFunction.KK
          ? processControlTabs
          : processControlTabs.filter((x) => x !== tabLabels.controlTasksDistribution),
    });
    items.push({
      label: tabLabels.controllToleranceRange,
      children: (
        <ToleranceRange
          isExaminerView={isExaminerView}
          id={sheetId}
          processType={AssessmentSessionProcessTypeId.Control}
          onClose={onClose}
        />
      ),
      parentLabel: tabLabels.controllProcess,
    });
    if ((isExaminerView && examinerFunction === ExaminerFunction.KK) || !isExaminerView)
      items.push({
        label: tabLabels.controlTasksDistribution,
        children: (
          <ControlTasksDistribution
            apiResponse={apiResponseControlTasksDistribution}
            items={controlTasksDistribution}
            onClose={onClose}
            reload={() => {
              setReloadControlTasks((prev) => !prev);
            }}
            onChange={onChange}
            isLoading={isControlTasksDistributionLoading}
            isExaminerView={Boolean(isExaminerView)}
          />
        ),
        parentLabel: tabLabels.controllProcess,
      });
    items.push({
      label: tabLabels.examinerBlockingLimits,
      children: (
        <ExaminerBlockingLimits
          apiResponse={JSON.stringify(apiResponseBlockingLimits)}
          items={examinerBlockingLimits}
          onClose={onClose}
          reload={() => {
            setReloadProcessLimits((prev) => !prev);
          }}
          onChange={onChange}
          isLoading={isExaminerBlockingLimitsLoading}
          isExaminerView={Boolean(isExaminerView)}
        />
      ),
      parentLabel: tabLabels.controllProcess,
    });
    items.push({
      label: tabLabels.examinerIndividualBlockingLimits,
      children: (
        <ExaminerIndividualBlockingLimitsTable
          examTaskGroups={examTaskGroups ?? []}
          onClose={onClose}
          isUserKKOrKGZ={isUserKK || isUserKGZ}
        />
      ),
      parentLabel: tabLabels.controllProcess,
    });
  }
  if (hasAdminSessionStatusManagePermissionQualificationProcess && isSheetConfigured) {
    items.push({
      label: tabLabels.qualificationProcess,
      collapsable: true,
      collapsedListItems: [tabLabels.qualificationToleranceRange, tabLabels.qualificationThreshold],
    });
    items.push({
      label: tabLabels.qualificationToleranceRange,
      children: (
        <ToleranceRange
          isExaminerView={isExaminerView}
          id={sheetId}
          processType={AssessmentSessionProcessTypeId.Qualification}
          onClose={onClose}
        />
      ),
      parentLabel: tabLabels.qualificationProcess,
    });
    items.push({
      label: tabLabels.qualificationThreshold,
      children: (
        <QualificationThreshold
          apiResponse={qualificationTresholdsApiResponse}
          items={qualificationThreshold}
          onClose={onClose}
          reload={() => {
            setReloadThreshold((prev) => !prev);
          }}
          onChange={onChange}
          isLoading={isQualificationThresholdLoading}
          isExaminerView={Boolean(isExaminerView)}
        />
      ),
      parentLabel: tabLabels.qualificationProcess,
    });
  }

  const reloadActiveDialogContent = (activeTab: string) => {
    switch (activeTab) {
      case tabLabels.sessionsParametersCardLimit:
        {
          setReloadCardLimits(true);
          setReloadControlTasks(true);
        }
        break;
      case tabLabels.sessionProcessLimits:
        {
          setReloadProcessLimits(true);
        }
        break;
      case tabLabels.controlTasksDistribution:
        {
          setReloadControlTasks(true);
        }
        break;
      case tabLabels.examinerBlockingLimits:
        {
          setReloadProcessLimits(true);
        }
        break;
      case tabLabels.examinerIndividualBlockingLimits:
        {
          setReloadProcessLimits(true);
        }
        break;
      case tabLabels.qualificationThreshold:
        {
          setReloadThreshold(true);
        }
        break;
      default:
        break;
    }
  };

  const getActiveTab = (): number => {
    const activeTabItem = items.find((item) => item.label === activeTab);
    if (activeTabItem) {
      return activeTabItem.collapsable
        ? items.map((e) => e.label).indexOf(activeTab) + 1
        : items.map((e) => e.label).indexOf(activeTab);
    }
    return 0;
  };

  const getActiveCollapsedTab = (): TabPanelCollapsedValue | undefined => {
    const activeTabItem = items.find((item) => item.label === activeTab);
    if (activeTabItem && activeTabItem.collapsable) {
      return { labelName: activeTab, index: 0 };
    } else return undefined;
  };

  return (
    <Grid container>
      <VerticalTabs
        items={items}
        selectedTab={getActiveTab()}
        selectedTabCollapsed={getActiveCollapsedTab()}
        reloadActiveDialogContent={reloadActiveDialogContent}
      />
    </Grid>
  );
};
